import { ExternalLink, FileText } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useToast } from "@/components/Toast";
import { errorText } from "@/lib/errors";
import { openTabInTap, type PendingTab } from "@/lib/openTab";

export type DocumentState = "pending" | "issued" | "unsupported" | "failed" | "none";

export interface DocumentLink {
  status: DocumentState;
  url?: string | null;
  number?: string | null;
}

/**
 * A PDF made by media-service (預り証, 買取明細書, 仕入明細書 — T07-41/42): one button that asks
 * pos-service for a 5-minute link (only when pressed — media records every link as a 電帳法
 * download) and opens it in a new tab (the tablet's print dialog). While the PDF is still being
 * made it says so; when it could not be made it says so in plain words and the button asks again.
 * The screen shows the new state by itself when it is ready (`pos.document.changed` → the hub →
 * the register's queries — R21).
 */
export function DocumentButton({
  label,
  number,
  state,
  open,
}: {
  label: string;
  /** The document's number once issued (DP-… / K-…). */
  number?: string | null;
  /** The last known state (from the list / detail the screen already has). */
  state?: DocumentState | null;
  /** Asks the server; returns the link / the current state. */
  open: () => Promise<DocumentLink>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // a link that arrived without a tab (the PDF became ready while we asked): a real link to tap
  const [ready, setReady] = useState<string | null>(null);
  const click = async () => {
    setBusy(true);
    setReady(null);
    // known to be ready → the tab is opened in the tap itself (lib/openTab), pointed at the
    // link below; not ready yet → no blank tab flashing open, a link to tap if it is ready now
    const tab: PendingTab | null = state === "issued" ? openTabInTap() : null;
    try {
      const link = await open();
      if (link.status === "issued" && link.url) {
        if (!tab?.show(link.url)) {
          setReady(link.url);
        }
        return;
      }
      tab?.close();
      toast.show(
        t(
          link.status === "unsupported" || link.status === "none"
            ? "document.unavailable"
            : link.status === "failed"
              ? "document.failed"
              : "document.pending",
          { name: label },
        ),
      );
    } catch (error) {
      tab?.close();
      toast.show(errorText(error, t), "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => void click()}
        disabled={busy}
        data-state={state ?? undefined}
        className="flex min-h-pos-touch items-center justify-center gap-2 rounded-md border px-4 text-lg disabled:opacity-50"
      >
        <FileText aria-hidden className="size-5 shrink-0" />
        <span>
          {label}
          {number ? <span className="ml-2 font-mono text-sm">{number}</span> : null}
          {state === "pending" ? (
            <span className="block text-sm text-muted-foreground">{t("document.making")}</span>
          ) : null}
          {state === "failed" ? (
            <span className="block text-sm text-destructive">{t("document.retry")}</span>
          ) : null}
        </span>
      </button>
      {ready ? (
        <a
          href={ready}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setReady(null)}
          className="flex min-h-pos-touch items-center justify-center gap-2 rounded-md bg-primary px-4 text-lg font-semibold text-primary-foreground"
        >
          <ExternalLink aria-hidden className="size-5 shrink-0" />
          {t("document.open", { name: label })}
        </a>
      ) : null}
    </div>
  );
}
